import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { VersionReadyEvent } from '@angular/service-worker';
import { PwaService } from '../../../core/services/pwa.service';
import { GlassCardComponent } from '../glass-card/glass-card.component';

@Component({
  selector: 'app-sw-update-notification',
  standalone: true,
  imports: [CommonModule, GlassCardComponent],
  template: `
    @if (updateAvailable()) {
      <app-glass-card tint="secondary" padding="md" class="update-notification" role="alert" aria-live="assertive">
        <div class="update-notification__content">
          <div class="update-notification__icon">
            <img src="/assets/icons/plumpy-refresh.png" alt="به‌روزرسانی" class="icon-plumpy" />
          </div>
          <div class="update-notification__text">
            <h4>نسخه جدید در دسترس است</h4>
            <p>برای استفاده از آخرین ویژگی‌ها و بهبودها، اپ را به‌روزرسانی کنید.</p>
          </div>
        </div>
        <div class="update-notification__actions">
          <button class="update-notification__later glass-interactive" (click)="dismiss()">
            بعداً
          </button>
          <button class="update-notification__update glass-interactive" (click)="update()">
            به‌روزرسانی الان
          </button>
        </div>
      </app-glass-card>
    }
  `,
  styles: [`
    .update-notification {
      position: fixed;
      bottom: calc(var(--space-xl) + env(safe-area-inset-bottom) + 70px);
      left: var(--space-md);
      right: var(--space-md);
      max-width: 480px;
      margin-inline: auto;
      z-index: 1000;
      animation: slide-up 0.4s var(--ease-out-spring) both;
    }

    @keyframes slide-up {
      from { opacity: 0; transform: translateY(20px); }
      to { opacity: 1; transform: translateY(0); }
    }

    .update-notification__content {
      display: flex;
      align-items: flex-start;
      gap: var(--space-md);
    }

    .update-notification__icon {
      width: 48px;
      height: 48px;
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      background: var(--glass-bg-tinted-secondary);
      border-radius: var(--radius-lg);
      box-shadow: var(--glass-shadow-glow-secondary);
    }

    .update-notification__icon img {
      width: 24px;
      height: 24px;
    }

    .update-notification__text {
      flex: 1;
      min-width: 0;
    }

    .update-notification__text h4 {
      margin: 0 0 var(--space-xs);
      @include mixins.text-h3;
      color: var(--text-on-glass-strong);
    }

    .update-notification__text p {
      margin: 0;
      font-size: 0.82rem;
      color: var(--text-on-glass-medium);
      @include mixins.text-caption;
    }

    .update-notification__actions {
      display: flex;
      justify-content: flex-end;
      gap: var(--space-sm);
      margin-top: var(--space-md);
      padding-top: var(--space-md);
      border-top: 1px solid var(--glass-border-soft);
    }

    .update-notification__later {
      padding: var(--space-xs) var(--space-md);
      border-radius: var(--radius-full);
      font-size: 0.82rem;
      color: var(--text-on-glass-medium);
      background: var(--glass-bg-light);
      @include mixins.glass-interactive;
      min-height: 36px;

      &:focus-visible {
        @include mixins.focus-ring-spring();
      }
    }

    .update-notification__update {
      padding: var(--space-xs) var(--space-lg);
      border-radius: var(--radius-full);
      font-size: 0.82rem;
      font-weight: 700;
      color: var(--text-on-glass-strong);
      background: var(--glass-bg-tinted-secondary);
      @include mixins.glass-interactive;
      min-height: 36px;
      box-shadow: var(--glass-shadow-glow-secondary);

      &:focus-visible {
        @include mixins.focus-ring-spring();
      }
    }
  `],
})
export class SwUpdateNotificationComponent implements OnInit, OnDestroy {
  private readonly pwaService = inject(PwaService);

  updateAvailable = signal<VersionReadyEvent | null>(null);

  ngOnInit(): void {
    this.pwaService.updateAvailable$.subscribe((event) => {
      this.updateAvailable.set(event);
    });
  }

  ngOnDestroy(): void {}

  update(): void {
    this.pwaService.applyUpdate();
  }

  dismiss(): void {
    this.updateAvailable.set(null);
  }
}